import { assertNever } from "@ondo/shared";
import { Navigate, useLocation } from "react-router";

import { LoadingScreen } from "../lib/Loading.tsx";
import { useAccount } from "../lib/account.ts";
import { parseRole, workspacePath } from "../routes/routes.model.ts";
import { ClaimStudioBanner } from "./ClaimStudioBanner.tsx";
import { SeatInvitationsBanner } from "./SeatInvitationsBanner.tsx";
import { DeclareCapacity } from "./DeclareCapacity.tsx";
import { WorkspaceChooser } from "./WorkspaceChooser.tsx";
import { WorkspaceShell } from "./WorkspaceShell.tsx";
import { ACCESS_REQUEST_URL, WORKSPACE_COPY } from "./workspace.constants.ts";
import { resolveWorkspace } from "./workspace.model.ts";
import "./workspace.css";

/**
 * `/workspaces` — which workspace to open, and what to say when there isn't one.
 *
 * Every rule about that lives in `workspace.model.ts`. What is left here is the
 * branch and the markup.
 *
 * The sign-in hint arrives as navigation state from `LandingRedirect` rather
 * than being read from storage again, which is what keeps it unable to outlive
 * one navigation. It is parsed on the way in for the same reason it always was:
 * history state is client-controlled, so it is a hint and never a claim.
 *
 * AN EMPTY CHOOSER IS A DOOR, NOT A DEAD END — OND-12. An account with no
 * workspace at all (the invited haulier, the new collector) is offered the
 * declare-a-capacity form in place of the old paragraph; the apply link
 * survives inside it, narrowed to what it is for. `replaceMe` is what makes the
 * door work: the route answers with the whole account, the page re-resolves,
 * and the chooser that was empty has choices on the same render.
 */
export function WorkspacesPage() {
  const { me, replaceMe, signOut } = useAccount();
  const location = useLocation();
  const intended = parseRole(
    (location.state as { intended?: unknown } | null)?.intended as string | undefined,
  );

  const view = resolveWorkspace({ me, intended });

  switch (view.kind) {
    case "loading":
      return (
        <WorkspaceShell onSignOut={signOut}>
          <LoadingScreen message={view.message} />
        </WorkspaceShell>
      );

    case "error":
      return (
        <WorkspaceShell onSignOut={signOut}>
          <h1 className="workspace__title">{view.title}</h1>
          <p className="workspace__note">{view.message}</p>
          {view.unreachable && (
            <p className="workspace__note">
              You are signed in — this is a connection problem, not an account
              one. If you are running ONDO locally, start it with{" "}
              <code>pnpm dev</code> from the repository root.
            </p>
          )}
        </WorkspaceShell>
      );

    case "unprovisioned":
      return (
        <WorkspaceShell onSignOut={signOut}>
          <h1 className="workspace__title">{view.title}</h1>
          <p className="workspace__note">{view.message}</p>
        </WorkspaceShell>
      );

    case "denied":
      return (
        <WorkspaceShell onSignOut={signOut} principalName={view.principalName}>
          <h1 className="workspace__title">{view.title}</h1>
          <ClaimStudioBanner replaceMe={replaceMe} />
          {/* OND-24, beside the studio's banner for the same reason: somebody
              reached by email is told on the first screen they land on rather
              than having to go looking. An empty list draws nothing, which is
              the answer for almost everybody. */}
          <SeatInvitationsBanner replaceMe={replaceMe} />
          <p className="workspace__note">{view.message}</p>

          {view.choices.length > 0 ? (
            <>
              <a className="workspace__apply" href={ACCESS_REQUEST_URL}>
                Apply for access <span aria-hidden="true">→</span>
              </a>

              <hr className="workspace__divider" />

              <h2 className="workspace__subtitle">{WORKSPACE_COPY.yourWorkspaces}</h2>
              <WorkspaceChooser choices={view.choices} />
            </>
          ) : (
            <>
              <p className="workspace__note">{WORKSPACE_COPY.noEntities}</p>
              <DeclareCapacity me={view.me} replaceMe={replaceMe} />
            </>
          )}
        </WorkspaceShell>
      );

    case "choosing":
      return (
        <WorkspaceShell onSignOut={signOut} principalName={view.principalName}>
          <h1 className="workspace__title">{view.title}</h1>
          {/* OND-7: the one place other than the verifier's inbox where a
              dormant studio is offered. Null for almost everybody, and null
              draws nothing. */}
          <ClaimStudioBanner replaceMe={replaceMe} />
          {/* OND-24. The chooser is where an invited colleague with nothing of
              their own lands, so it is the screen that most needs this: before
              OND-24 such an account had no workspace and nowhere to be told. */}
          <SeatInvitationsBanner replaceMe={replaceMe} />
          {view.choices.length > 0 ? (
            <WorkspaceChooser choices={view.choices} />
          ) : (
            <DeclareCapacity me={view.me} replaceMe={replaceMe} />
          )}
        </WorkspaceShell>
      );

    case "open":
      // Reached by arriving here with a hint that resolves — signing in via a
      // role you hold should not stop at a chooser it would answer for you.
      return <Navigate to={workspacePath(view.workspace)} replace />;

    default:
      return assertNever(view);
  }
}
